import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaUsers, FaPlus, FaMinus, FaPaw } from 'react-icons/fa';
import './GuestPetSelector.css';

const GuestPetSelector = ({ onApply, initialValues = null, isOpen: controlledOpen, onOpenChange }) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;
  const setIsOpen = (value) => {
    if (isControlled && onOpenChange) onOpenChange(!!value);
    else setInternalOpen(!!value);
  };
  const [isMobile, setIsMobile] = useState(false);
  const [adults, setAdults] = useState(initialValues?.adults || 1);
  const [children, setChildren] = useState(initialValues?.children || 0);
  const [infants, setInfants] = useState(initialValues?.infants || 0);
  const [rooms, setRooms] = useState(initialValues?.rooms || 1);
  const [pets, setPets] = useState(initialValues?.pets || 0);
  const [warnings, setWarnings] = useState({});
  
  const dropdownRef = useRef(null);
  const maxGuestsPerRoom = 4;
  const maxTotalGuests = rooms * maxGuestsPerRoom;

  // Detect mobile once on mount and on resize (avoids using window in JSX directly)
  useEffect(() => {
    const updateIsMobile = () => {
      if (typeof window !== 'undefined') {
        setIsMobile(window.innerWidth <= 768);
      }
    };
    updateIsMobile();
    window.addEventListener('resize', updateIsMobile);
    return () => window.removeEventListener('resize', updateIsMobile);
  }, []);

  // Sync with initialValues when they change externally
  useEffect(() => {
    if (initialValues) {
      setAdults(initialValues.adults || 1);
      setChildren(initialValues.children || 0);
      setInfants(initialValues.infants || 0);
      setRooms(initialValues.rooms || 1);
      setPets(initialValues.pets || 0);
    }
  }, [initialValues]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Only prevent body scroll on mobile to avoid page blur
      if (isMobile) {
        document.body.style.overflow = 'hidden';
      }
    } else {
      // Restore body scroll
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [isOpen, isMobile]);

  // Validation
  useEffect(() => {
    const newWarnings = {};

    // Infants must not exceed adults
    if (infants > adults) {
      newWarnings.infants = `Infants cannot exceed adults (max ${adults})`;
    }

    // Total guests must not exceed room capacity
    const totalGuests = adults + children;
    if (totalGuests > maxTotalGuests) {
      newWarnings.capacity = `Maximum ${maxTotalGuests} guests for ${rooms} room${rooms > 1 ? 's' : ''} (${maxGuestsPerRoom} per room)`;
    }

    setWarnings(newWarnings);
  }, [adults, children, infants, rooms, maxTotalGuests]);

  const handleIncrement = (type) => {
    switch (type) {
      case 'adults':
        if (adults < 16) setAdults(prev => prev + 1);
        break;
      case 'children':
        if (children < 16) setChildren(prev => prev + 1);
        break;
      case 'infants':
        if (infants < adults && infants < 5) setInfants(prev => prev + 1);
        break;
      case 'rooms':
        if (rooms < 10) setRooms(prev => prev + 1);
        break;
      case 'pets':
        if (pets < 5) setPets(prev => prev + 1);
        break;
      default:
        break;
    }
  };

  const handleDecrement = (type) => {
    switch (type) {
      case 'adults':
        if (adults > 1) setAdults(prev => prev - 1);
        break;
      case 'children':
        if (children > 0) setChildren(prev => prev - 1);
        break;
      case 'infants':
        if (infants > 0) setInfants(prev => prev - 1);
        break;
      case 'rooms':
        if (rooms > 1) setRooms(prev => prev - 1);
        break;
      case 'pets':
        if (pets > 0) setPets(prev => prev - 1);
        break;
      default:
        break;
    }
  };

  const handleApply = () => {
    const totalGuests = adults + children;
    if (totalGuests > maxTotalGuests) {
      return; // Don't apply if validation fails
    }
    if (infants > adults) {
      return; // Don't apply if validation fails
    }

    const values = { adults, children, infants, rooms, pets };
    if (onApply) {
      onApply(values);
    }
    setIsOpen(false);
  };

  const handleClear = () => {
    setAdults(1);
    setChildren(0);
    setInfants(0);
    setRooms(1);
    setPets(0);
    setWarnings({});
  };

  const generateSummary = () => {
    const parts = [];
    
    // Adults
    if (adults > 0) {
      parts.push(`${adults} ${adults === 1 ? 'Adult' : 'Adults'}`);
    }
    
    // Children
    if (children > 0) {
      parts.push(`${children} ${children === 1 ? 'Child' : 'Children'}`);
    }
    
    // Infants
    if (infants > 0) {
      parts.push(`${infants} ${infants === 1 ? 'Infant' : 'Infants'}`);
    }
    
    // Rooms
    if (rooms > 0) {
      parts.push(`${rooms} ${rooms === 1 ? 'Room' : 'Rooms'}`);
    }
    
    // Pets
    if (pets > 0) {
      parts.push(`${pets} ${pets === 1 ? 'Pet' : 'Pets'}`);
    }

    return parts.length > 0 ? parts.join(' · ') : '1 Adult · 1 Room';
  };

  const canIncrement = (type) => {
    const totalGuests = adults + children;
    switch (type) {
      case 'adults':
        return adults < 16 && totalGuests < maxTotalGuests;
      case 'children':
        return children < 16 && totalGuests < maxTotalGuests;
      case 'infants':
        return infants < adults && infants < 5;
      case 'rooms':
        return rooms < 10;
      case 'pets':
        return pets < 5;
      default:
        return false;
    }
  };

  const canDecrement = (type) => {
    switch (type) {
      case 'adults':
        return adults > 1;
      case 'children':
        return children > 0;
      case 'infants':
        return infants > 0;
      case 'rooms':
        return rooms > 1;
      case 'pets':
        return pets > 0;
      default:
        return false;
    }
  };

  const CounterControl = ({ label, value, onIncrement, onDecrement, canInc, canDec, subtitle, warning }) => (
    <div className="counter-control">
      <div className="counter-header">
        <span className="counter-label">{label}</span>
        {subtitle && <span className="counter-subtitle">{subtitle}</span>}
      </div>
      <div className="counter-buttons">
        <button
          type="button"
          className={`counter-btn ${!canDec ? 'disabled' : ''}`}
          onClick={onDecrement}
          disabled={!canDec}
          aria-label={`Decrease ${label}`}
        >
          <FaMinus />
        </button>
        <span className="counter-value">{value}</span>
        <button
          type="button"
          className={`counter-btn ${!canInc ? 'disabled' : ''}`}
          onClick={onIncrement}
          disabled={!canInc}
          aria-label={`Increase ${label}`}
        >
          <FaPlus />
        </button>
      </div>
      {warning && <span className="warning-text">{warning}</span>}
    </div>
  );

  const handleTriggerClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleTriggerTouch = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  return (
    <div className="guest-pet-selector" ref={dropdownRef}>
      <div
        className="selector-trigger"
        onClick={handleTriggerClick}
        onTouchStart={handleTriggerTouch}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsOpen(!isOpen);
          }
        }}
      >
        <FaUsers className="selector-icon" />
        <span className="selector-text">{generateSummary()}</span>
      </div>

      <AnimatePresence>
        {isOpen && (
          <>
            {/* Only show backdrop on mobile */}
            {isMobile && (
              <motion.div
                className="dropdown-backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsOpen(false)}
              />
            )}
            <motion.div
              className="selector-dropdown"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
            <div className="dropdown-content">
              <div className="selector-section">
                <h3 className="section-title">Guests</h3>
                <div className="selector-section-content">
                  <CounterControl
                    label="Adults"
                    value={adults}
                    onIncrement={() => handleIncrement('adults')}
                    onDecrement={() => handleDecrement('adults')}
                    canInc={canIncrement('adults')}
                    canDec={canDecrement('adults')}
                  />

                  <CounterControl
                    label="Children"
                    subtitle="Ages 2-17"
                    value={children}
                    onIncrement={() => handleIncrement('children')}
                    onDecrement={() => handleDecrement('children')}
                    canInc={canIncrement('children')}
                    canDec={canDecrement('children')}
                  />

                  <CounterControl
                    label="Infants"
                    subtitle="Under 2"
                    value={infants}
                    onIncrement={() => handleIncrement('infants')}
                    onDecrement={() => handleDecrement('infants')}
                    canInc={canIncrement('infants')}
                    canDec={canDecrement('infants')}
                    warning={warnings.infants}
                  />
                </div>
                {warnings.capacity && (
                  <div className="warning-message">
                    <span className="warning-icon">⚠</span>
                    <span>{warnings.capacity}</span>
                  </div>
                )}
              </div>

              <div className="selector-section">
                <h3 className="section-title">Rooms</h3>
                <div className="selector-section-content">
                  <CounterControl
                    label="Rooms"
                    value={rooms}
                    onIncrement={() => handleIncrement('rooms')}
                    onDecrement={() => handleDecrement('rooms')}
                    canInc={canIncrement('rooms')}
                    canDec={canDecrement('rooms')}
                  />
                </div>
              </div>

              <div className="selector-section">
                <h3 className="section-title">
                  <FaPaw className="paw-icon" />
                  Pets
                </h3>
                <div className="selector-section-content">
                  <CounterControl
                    label="Pets"
                    value={pets}
                    onIncrement={() => handleIncrement('pets')}
                    onDecrement={() => handleDecrement('pets')}
                    canInc={canIncrement('pets')}
                    canDec={canDecrement('pets')}
                  />
                </div>
                {pets > 0 && (
                  <div className="pet-message">
                    <FaPaw className="paw-icon-small" />
                    <span>Only pet-friendly properties will be shown</span>
                  </div>
                )}
              </div>

              <div className="dropdown-actions">
                <button
                  type="button"
                  className="btn-clear"
                  onClick={handleClear}
                >
                  Clear
                </button>
                <button
                  type="button"
                  className="btn-apply"
                  onClick={handleApply}
                  disabled={Object.keys(warnings).length > 0}
                >
                  Apply
                </button>
              </div>
            </div>
          </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default GuestPetSelector;

